import { Inject, Injectable } from '@angular/core';
import { NgModel } from '@angular/forms';
import { DEFAULT_TIMEZONE, RouteNames } from '@nx-ng/constants';
import { SwiftCardState } from '@nx-ng/types';
import { getNowUTCDate } from '@nx-ng/util';
import { zonedTimeToUtc } from 'date-fns-tz';

@Injectable({
  providedIn: 'root',
})
export class FormErrorService {
  getFirstNameError(firstName: NgModel) {
    return firstName.touched &&
      (firstName?.errors?.required || firstName?.errors?.pattern)
      ? 'customer_details_first_name_error_text'
      : '';
  }

  getLastNameError = (lastName: NgModel) => {
    return lastName.touched &&
      (lastName?.errors?.required || lastName?.errors?.pattern)
      ? 'customer_details_last_name_error_text'
      : '';
  };

  getMobileError(control: NgModel) {
    return control.touched &&
      (control?.errors?.required || control?.errors?.pattern)
      ? 'm_ticket_mobile_error_text'
      : '';
  }

  getEmailError(control: NgModel) {
    return control.touched && control?.errors?.required
      ? 'm_ticket_email_error_text'
      : '';
  }

  getExistingSwiftCardNumberError(control: NgModel) {
    return control.touched && control?.errors?.required
      ? 'swift_card_card_number_please_select'
      : '';
  }

  getRegisterSwiftCardNumberError(control: NgModel) {
    if (!control.touched) {
      return '';
    }

    if (control?.errors?.required || control?.errors?.pattern) {
      return 'swift_card_card_number_required';
    } else if (control?.errors?.unique) {
      return 'swift_card_unique_for_all_applicants';
    }

    return '';
  }

  getTicketApplicantError(control: NgModel) {
    if (!control.touched) {
      return '';
    }

    if (control.errors?.required) {
      return 'ticket_applicant_error';
    } else if (control.errors?.unique) {
      return 'ticket_applicant_one_ticket_quantity_for_self_error';
    }

    return '';
  }

  getDateOfBirthError(control: NgModel) {
    if (!control.touched) {
      return '';
    }
    if (control?.errors?.required) {
      return 'customer_details_date_of_birth_required_error_text';
    } else if (control?.errors?.maxDate) {
      return control.value >= zonedTimeToUtc(getNowUTCDate(), DEFAULT_TIMEZONE)
        ? 'customer_details_date_of_birth_maxdate_error_text'
        : 'date_of_birth_not_valid_for_ticket_description_1';
    } else if (control?.errors?.minDate) {
      return 'date_of_birth_not_valid_for_ticket_description_1';
    }
    return '';
  }

  getActivationDateError(
    control: NgModel,
    swiftcardState: SwiftCardState | undefined
  ) {
    if (!control.touched) {
      return '';
    }
    if (control?.errors?.required) {
      return 'swift_card_activation_date_required_error_text';
    } else if (control?.errors?.minDate) {
      if (swiftcardState === SwiftCardState.New) {
        return 'swift_card_activation_date_new_mindate_error_text';
      } else {
        return 'swift_card_activation_date_existing_mindate_error_text';
      }
    }
    return '';
  }

  getApprenticeError(control: NgModel) {
    return control.touched && control?.errors?.required
      ? 'child_education_details_apprentice_error_text'
      : '';
  }

  getEmploymentError(control: NgModel) {
    return control.touched && control?.errors?.required
      ? 'child_education_details_employment_error_text'
      : '';
  }

  getEducationError(control: NgModel) {
    return control.touched && control?.errors?.required
      ? 'child_education_details_education_error_text'
      : '';
  }

  getStartDateError = (control: NgModel) => {
    if (!control.touched) {
      return '';
    }

    if (control?.errors?.required) {
      return 'm_ticket_start_date_required_error_text';
    } else if (control?.errors?.minDate) {
      return 'm_ticket_start_date_mindate_error_text';
    }
    return '';
  };

  getPreferencesError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'customer_details_preferences_required'
      : '';
  };

  getAccountHolderNameError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'direct_debit_account_holder_name_error'
      : '';
  };

  getAddress1Error = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'customer_details_address_1_error_text'
      : '';
  };

  getTownError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'customer_details_town_error_text'
      : '';
  };

  getPostCodeError = (control: NgModel) => {
    if (!control.touched) {
      return '';
    }
    if (control.errors?.required) {
      return 'customer_details_postcode_required_error_text';
    } else if (control.errors?.pattern) {
      return 'customer_details_postcode_invalid_error_text';
    }

    return '';
  };

  getSortCodeError = (control: NgModel) => {
    return control.touched &&
      (control?.errors?.required || control?.errors?.pattern)
      ? 'direct_debit_sortcode_error'
      : '';
  };

  getAccountNumberError = (control: NgModel) => {
    return control.touched &&
      (control?.errors?.required || control?.errors?.pattern)
      ? 'direct_debit_accountnumber_error'
      : '';
  };

  getConfirmCreditCheckError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'direct_debit_credit_check_agreement_error'
      : '';
  };

  getAccountHolderMandateError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'direct_debit_credit_account_holder_mandate_error'
      : '';
  };

  getInstitutionError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'student_details_institution_error'
      : '';
  };

  getCourseError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'student_details_course_error'
      : '';
  };

  getCourseEndDateError = (control: NgModel) => {
    if (!control.touched) {
      return '';
    }
    if (control?.errors?.required) {
      return 'student_details_courseEndDate_required_error';
    } else if (control?.errors?.minDate) {
      return 'student_details_courseEndDate_mindate_error';
    }
    return '';
  };

  getStudentIdError = (control: NgModel) => {
    return control.touched && control?.errors?.required
      ? 'student_details_id_error'
      : '';
  };
}
